import { canOpenResource } from './access';
import { policyAllows } from './modules';
import type { Context, Entity, Snapshot } from './types';
import { canAccessEntity } from './visibility';

export interface CatalogRecord {
  uuid: string;
  title: string;
  revision: number;
  campus: string;
  createdAt: string;
  archived: boolean;
  locked: boolean;
  contentAccessible: boolean;
  references: number;
  bytes: number | null;
}
export function recordingReferences(state: Snapshot, uuid: string): number {
  const mentions = (value: unknown): boolean =>
    typeof value === 'string'
      ? value === uuid || value.includes(uuid)
      : Array.isArray(value)
        ? value.some(mentions)
        : !!value &&
          typeof value === 'object' &&
          Object.values(value).some(mentions);
  return (
    state.entities.filter(
      (source) =>
        source.uuid !== uuid &&
        !['audit', 'message'].includes(source.kind) &&
        (source.sourceUuid === uuid ||
          mentions(source.evidence) ||
          mentions(source.fields)),
    ).length + Object.values(state.domainData || {}).filter(mentions).length
  );
}
/** Governance receives metadata only, including recoverable archived objects. */
export function dataCatalog(
  state: Snapshot,
  basic: (row: Entity, context: Context) => boolean,
): CatalogRecord[] {
  if (!canOpenResource('dataPolicy', state))
    throw new Error('当前业务视角无权查询数据目录');
  return state.entities
    .filter(
      (row) =>
        row.kind === 'recording' &&
        (state.context.campus === '全部校区' ||
          row.campus === state.context.campus),
    )
    .map((row) => {
      const contentAccessible = canAccessEntity(state, row, basic);
      const references = recordingReferences(state, row.uuid);
      return {
        uuid: row.uuid,
        revision: row.revision,
        title: contentAccessible ? row.title : '受限录音资料',
        campus: row.campus,
        createdAt: row.createdAt,
        archived: row.fields?.softDeleted === 'true',
        locked: row.fields?.retentionLock === 'true',
        contentAccessible,
        references,
        bytes: row.audioBlob?.size ?? null,
      };
    });
}
export function permissionPreview(
  state: Snapshot,
  basic: (row: Entity, context: Context) => boolean,
  context: Context,
  uuid: string,
) {
  if (!canOpenResource('permission', state))
    throw new Error('无权验证业务权限');
  const source = state.entities.find((row) => row.uuid === uuid);
  // Preview reports a decision only; it never returns another role's content.
  const projected = { ...state, context };
  const policy = source ? policyAllows(projected, source.kind, 'read') : false;
  const scope =
    !!source &&
    (context.campus === '全部校区' || source.campus === context.campus);
  const allowed = !!source && canAccessEntity(projected, source, basic);
  return {
    allowed,
    policy,
    scope,
    reason: !source
      ? '对象不存在'
      : !scope
        ? '对象不在所选组织范围'
        : !policy
          ? '已发布策略禁止读取'
          : allowed
            ? '角色、范围、内容与引用权限均满足'
            : '角色内容权限、本人范围或来源授权不满足',
  };
}

export function configurationSummary(state: Snapshot) {
  if (
    !canOpenResource('model', state) &&
    !canOpenResource('subscription', state)
  )
    throw new Error('无权查询处理容量');
  const jobs = state.entities.filter(
    (r) =>
      r.kind === 'job' &&
      (state.context.campus === '全部校区' ||
        r.campus === state.context.campus),
  );
  return {
    total: jobs.length,
    used: jobs.filter((r) => !['failed', 'cancelled'].includes(r.status))
      .length,
    running: jobs.filter((r) => ['running', 'queued'].includes(r.status))
      .length,
    failed: jobs.filter((r) => r.status === 'failed').length,
    campuses: [...new Set(jobs.map((r) => r.campus))].map((campus) => ({
      campus,
      used: jobs.filter(
        (r) =>
          r.campus === campus && !['failed', 'cancelled'].includes(r.status),
      ).length,
    })),
  };
}
